/**
 * useMyWssState Hook
 * Centralizes state management for My WSS feature
 * Combines Redux selectors and local state
 */

import { useState } from "react";
import { useSelector, shallowEqual } from "react-redux";
import {
  CLUSTER_CODE_PLACEHOLDER,
  TERRITORY_PLACEHOLDER,
  WSS_PLACEHOLDER,
  LOADING_STATES,
} from "../utils/wssConstants";

export const useMyWssState = () => {
  // ===== Redux Selectors =====
  const territoryOptions = useSelector(
    (state) => state?.territory?.territoryDetails,
    shallowEqual
  );

  const userType = useSelector(
    (state) =>
      state?.userType?.type !== "Other"
        ? state?.userType?.type
        : state?.userDetails?.details?.Title,
    shallowEqual
  );

  const clusterCodeOption = useSelector(
    (state) => state?.clusterCode?.clusterCodeList,
    shallowEqual
  );

  const tourPlanDefaultData = useSelector(
    (state) => state?.upcomingTourPlan?.upcomingTourDetails?.records,
    shallowEqual
  );

  const salesGroupCode = useSelector(
    (state) =>
      state?.userDetails?.details?.Sales_Group_Code__c
        ? state?.userDetails?.details?.Sales_Group_Code__c
        : state?.SelectedSalesGroupCode?.SalesGroupCode,
    shallowEqual
  );

  const divisionCode = useSelector(
    (state) => state?.userDetails?.details?.Division,
    shallowEqual
  );

  // ===== Local State =====
  // WSS selection
  const [wssOption, setWssOption] = useState([]);
  const [selectLatLong, setSelectLatLong] = useState(WSS_PLACEHOLDER);
  const [wss, setWss] = useState("");
  const [customerCode, setCustomerCode] = useState(0);
  const [accuracy, setAccuracy] = useState(0);
  const [mtpCheckIn, setMtpCheckIn] = useState("");

  // Territory selection
  const [selectedTerritory, setSelectedTerritory] = useState(TERRITORY_PLACEHOLDER);
  const [territoryPickList, setTerritoryPickList] = useState([]);
  const [territoryCheckIn, setTerritoryCheckIn] = useState("");
  const [sh3CodeForTerritory, setSH3CodeForTerritory] = useState("");

  // Cluster selection
  const [cluster, setCluster] = useState(CLUSTER_CODE_PLACEHOLDER);

  // UI State
  const [loading, setLoading] = useState(false);
  const [isWssData, setIsWssData] = useState(LOADING_STATES.LOADING);

  // Modals
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isAuditPopupOpen, setIsAuditPopupOpen] = useState(false);
  const [isMyDiaryOpen, setIsMyDiaryOpen] = useState(false);
  const [isMySROpen, setIsMySROpen] = useState(false);
  const [isPreviousObservationOpen, setIsPreviousObservationOpen] = useState(false);

  // Audit popup data (HO Auditor only)
  const [auditPopupData, setAuditPopupData] = useState({
    retailCounter: "",
    nonPerforming: "",
    nonPerformingCompany: "",
  });

  // Check-in related
  const [checkinPayLoad, setCheckinPayLoad] = useState({});
  const [newCheckinId, setNewCheckinId] = useState({});
  const [recordId, setRecordId] = useState("");

  // Audit eligibility (derived from check-in history)
  // Default true: if history hasn't loaded yet, err on the side of showing audit popup
  const [lastCheckInDate, setLastCheckInDate] = useState(null);
  const [isAuditEligible, setIsAuditEligible] = useState(true);

  // SH3 specific - MyWssSummary
  const [getSummeryList, setGetSummeryList] = useState({});

  // Quarterly form flag (unused but kept for future)
  const [isQuaterlyFlag, setIsQuaterlyFlag] = useState(true);

  // ===== Return all state and setters =====
  return {
    // Redux state
    territoryOptions,
    userType,
    clusterCodeOption,
    tourPlanDefaultData,
    salesGroupCode,
    divisionCode,

    // WSS state
    wssOption,
    setWssOption,
    selectLatLong,
    setSelectLatLong,
    wss,
    setWss,
    customerCode,
    setCustomerCode,
    accuracy,
    setAccuracy,
    mtpCheckIn,
    setMtpCheckIn,

    // Territory state
    selectedTerritory,
    setSelectedTerritory,
    territoryPickList,
    setTerritoryPickList,
    territoryCheckIn,
    setTerritoryCheckIn,
    sh3CodeForTerritory,
    setSH3CodeForTerritory,

    // Cluster state
    cluster,
    setCluster,

    // UI state
    loading,
    setLoading,
    isWssData,
    setIsWssData,

    // Modals
    isSubmitModalOpen,
    setIsSubmitModalOpen,
    isAuditPopupOpen,
    setIsAuditPopupOpen,
    isMyDiaryOpen,
    setIsMyDiaryOpen,
    isMySROpen,
    setIsMySROpen,
    isPreviousObservationOpen,
    setIsPreviousObservationOpen,

    // Audit popup
    auditPopupData,
    setAuditPopupData,

    // Check-in related
    checkinPayLoad,
    setCheckinPayLoad,
    newCheckinId,
    setNewCheckinId,
    recordId,
    setRecordId,

    // Audit eligibility
    lastCheckInDate,
    setLastCheckInDate,
    isAuditEligible,
    setIsAuditEligible,

    // SH3 summary
    getSummeryList,
    setGetSummeryList,

    // Quarterly flag
    isQuaterlyFlag,
    setIsQuaterlyFlag,
  };
};

export default useMyWssState;
